<template>
  <div class="p-3 pb-20 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-center p-3 gap-4 bg-gray-900 bg-opacity-80 rounded border-gray-800 mb-1 border">
      <h2 class="text-lg font-semibold text-indigo-300 flex items-center gap-2">
        <i class="fas fa-map text-base"></i> แผนที่ ({{ filteredMaps.length }})
      </h2>
      <div class="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
        <select
          v-model="filterType"
          @change="applyFilters"
          class="px-4 py-2 bg-gray-900 text-indigo-200 rounded-md text-sm border border-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition duration-200 w-full sm:w-40"
        >
          <option value="สถานที่">สถานที่</option>
          <option value="โลกวิญญาณ">โลกวิญญาณ</option>
        </select>
        <button 
          v-if="isAdmin"
          @click="toggleModal = 'AddMap'"
          :disabled="isAddingMap"
          class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-md text-white text-sm font-semibold transition duration-200 flex items-center gap-2 group"
        >
          <i v-if="!isAddingMap" class="fas fa-plus text-xs group-hover:scale-110 transition-transform duration-200"></i>
          <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <span>สร้างแผนที่สถานที่</span>
          <span class="tooltip">เพิ่มแผนที่สถานที่ใหม่ในระบบ</span>
        </button>
        <button 
          v-if="isAdmin"
          @click="toggleModal = 'AddSupernaturalMap'"
          :disabled="isAddingMap"
          class="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-md text-white text-sm font-semibold transition duration-200 flex items-center gap-2 group"
        >
          <i v-if="!isAddingMap" class="fas fa-plus text-xs group-hover:scale-110 transition-transform duration-200"></i>
          <div v-else class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <span>สร้างแผนที่โลกวิญญาณ</span>
          <span class="tooltip">เพิ่มแผนที่โลกวิญญาณใหม่ในระบบ</span>
        </button>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="p-3 bg-red-900 text-red-200 rounded-md text-center text-sm mb-4">
      <i class="fas fa-exclamation-circle mr-2"></i>{{ error }}
    </div>

    <!-- Map Cards -->
    <main>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div 
          v-for="map in filteredMaps" 
          :key="map.id" 
          @click="showDetails(map)"
          class="bg-gray-900 rounded-lg shadow-md overflow-hidden transition-all duration-200 cursor-pointer relative group hover:bg-gray-800 hover:shadow-lg hover:border-indigo-400 border border-gray-600"
        >
          <!-- Image and Info Overlay -->
          <div class="relative w-full h-48">
            <img 
              :src="map.imageUrl" 
              :alt="map.name" 
              class="w-full h-full object-cover"
              @error="handleImageError"
            />
            <!-- Info Overlay at Bottom -->
            <div class="absolute bottom-0 left-0 p-4 w-full">
              <div v-if="map.requiredCoins || map.requiredGold || map.requiredDiamonds || map.requiredCash" class="flex flex-wrap gap-2 font-bold">
                <p v-if="map.requiredCoins" class="flex items-center gap-1 font-bold">
                  <i class="fas fa-coins text-gray-100 text-xs"></i> {{ map.requiredCoins }}
                </p>
                <p v-if="map.requiredGold" class="flex items-center gap-1">
                  <i class="fas fa-g text-yellow-500 text-xs"></i> {{ map.requiredGold }}
                </p>
                <p v-if="map.requiredDiamonds" class="flex items-center gap-1">
                  <i class="fas fa-gem text-blue-300 text-xs font-bold"></i> {{ map.requiredDiamonds }}
                </p>
                <p v-if="map.requiredCash" class="flex items-center gap-1">
                  <i class="fas fa-money-bill text-green-300 text-xs"></i> {{ map.requiredCash }}
                </p>
              </div>
              <h3 class="text-base font-semibold text-indigo-300 mb-1">{{ map.name }}</h3>
              <div class="text-xs text-indigo-200 space-y-1">
                <p class="flex items-center gap-1">
                  ประเภท: {{ map.type || 'สถานที่' }}
                </p>
                <p class="flex items-center gap-1">
                  เลเวลขั้นต่ำ: {{ map.requiredLevel || 0 }}
                </p>
                <p v-if="map.type === 'สถานที่'" class="flex items-center gap-1">
                  ซอมบี้: {{ map.minZombies || 0 }} - {{ map.maxZombies || 0 }}
                </p>
                <p v-else-if="map.type === 'โลกวิญญาณ'" class="flex items-center gap-1">
                  วิญญาณ: {{ map.minGhosts || 0 }} - {{ map.maxGhosts || 0 }}
                </p>
              </div>
            </div>
          </div>

          <!-- Admin Buttons -->
          <div v-if="isAdmin" class="p-3 bg-gray-900 bg-opacity-80 border-t border-gray-800">
            <button 
              @click.stop="editMap(map)" 
              class="w-full px-3 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-md text-white text-sm flex items-center justify-center gap-2 transition duration-200 group"
            >
              <i class="fas fa-edit text-xs"></i> แก้ไข
              <span class="tooltip">แก้ไขข้อมูลแผนที่</span>
            </button>
          </div>
        </div>
      </div>

      <!-- No Maps Message -->
      <div v-if="filteredMaps.length === 0 && !isLoading" class="text-center text-indigo-200 mt-8 text-sm">
        <i class="fas fa-exclamation-circle mr-2"></i> ยังไม่มีแผนที่ในระบบ
      </div>

      <!-- Loading Spinner -->
      <div v-if="isLoading" class="flex flex-col items-center justify-center mt-8">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
        <p class="text-indigo-200 text-sm mt-2">กำลังโหลด...</p>
      </div>
    </main>

    <!-- Modals -->
    <AddMapModal 
      v-if="toggleModal === 'AddMap'" 
      @cancelModal="toggleModal = ''" 
      @mapAdded="resetAndFetch" 
      class="modal"
    />
    <AddSupernaturalMapModal 
      v-if="toggleModal === 'AddSupernaturalMap'" 
      @cancelModal="toggleModal = ''" 
      @mapAdded="resetAndFetch" 
      class="modal"
    />
    <MapDetailsModal 
      v-if="toggleModal === 'ShowDetails' && selectedMap && selectedMap.type === 'สถานที่'" 
      :map="selectedMap" 
      @close="closeDetailsModal" 
      class="modal"
    />
    <SupernaturalMapDetailsModal 
      v-if="toggleModal === 'ShowDetails' && selectedMap && selectedMap.type === 'โลกวิญญาณ'" 
      :map="selectedMap" 
      @close="closeDetailsModal" 
      class="modal"
    />
    <EditMapModal 
      v-if="toggleModal === 'EditMap' && selectedMap && selectedMap.type === 'สถานที่'" 
      :map="selectedMap" 
      @cancelModal="toggleModal = ''" 
      @mapUpdated="resetAndFetch" 
      @mapDeleted="resetAndFetch"
      class="modal"
    />
    <EditSupernaturalMapModal 
      v-if="toggleModal === 'EditMap' && selectedMap && selectedMap.type === 'โลกวิญญาณ'" 
      :map="selectedMap" 
      @cancelModal="toggleModal = ''" 
      @mapUpdated="resetAndFetch" 
      @mapDeleted="resetAndFetch"
      class="modal"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { projectFirestore } from '@/firebase/config';
import AddMapModal from '../components/admin/AddMapModal.vue';
import AddSupernaturalMapModal from '../components/admin/AddSupernaturalMapModal.vue';
import MapDetailsModal from '../components/MapDetailsModal.vue';
import SupernaturalMapDetailsModal from '../components/SupernaturalMapDetailsModal.vue';
import EditMapModal from '../components/admin/EditMapModal.vue';
import EditSupernaturalMapModal from '../components/admin/EditSupernaturalMapModal.vue';
import getUser from '@/composables/getUser';

export default {
  components: { 
    AddMapModal, 
    AddSupernaturalMapModal, 
    MapDetailsModal, 
    SupernaturalMapDetailsModal, 
    EditMapModal, 
    EditSupernaturalMapModal 
  },
  setup() {
    const toggleModal = ref('');
    const maps = ref([]);
    const selectedMap = ref(null);
    const isLoading = ref(false);
    const isAddingMap = ref(false);
    const isAdmin = ref(false);
    const filterType = ref('สถานที่');
    const error = ref(null);
    const initialFetchDone = ref(false);

    // Define the custom order for maps
    const customMapOrder = {
      location: [
        'ตรอกซอย', 'ตลาด', 'สนามกอล์ฟ', 'โรงเรียน', 'มหาวิทยาลัย', 'โกดัง', 'รถไฟฟ้าใต้ดิน',
        'โรงพยาบาล', 'สยามสแควร์', 'หมู่บ้านคนรวย', 'สวนสนุก', 'โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า',
        'สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'ราชมังคลากีฬาสถาน',
        'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'พระบรมมหาราชวัง', 'ป่าช้า', 'สุสานนักรบโบราณ','ตึกความมันคงแห่งชาติ',
        'โรงเก็บศพจักรกล', 'ตึกปฏิบัติการพันธุกรรม','เหมืองแร่ร้าง','ศูนย์วิจัยสิ่งมีชีวิตกลายพันธุ์','สถานกักกันสิ่งมีชีวิตกลายพันธุ์'
      ],
      supernatural: [
        'ตลาดวิญญาณ', 'สนามกอล์ฟวิญญาณ', 'โรงเรียนวิญญาณ', 'มหาวิทยาลัยวิญญาณ', 'รถไฟฟ้าใต้ดินวิญญาณ',
        'โรงพยาบาลวิญญาณ', 'หมู่บ้านคนรวยวิญญาณ', 'วัดวิญญาณ', 'สวนสนุกวิญญาณ',
        'โรงงานอุตสาหกรรมวิญญาณ', 'ห้างสรรพสินค้าวิญญาณ', 'สถานีดับเพลิงวิญญาณ', 'ค่ายทหารวิญญาณ', 'พระบรมมหาราชวังวิญญาณ','ราชมังคลากีฬาสถานวิญญาณ'
      ]
    };

    // Computed property to filter maps based on type
    const filteredMaps = computed(() => {
      let filtered = maps.value.filter(map => map.type === filterType.value);

      // Sort maps based on customMapOrder
      filtered.sort((a, b) => {
        const order = filterType.value === 'สถานที่' ? customMapOrder.location : customMapOrder.supernatural;
        const cleanNameA = (a.name || '').trim();
        const cleanNameB = (b.name || '').trim();
        const indexA = order.indexOf(cleanNameA);
        const indexB = order.indexOf(cleanNameB);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return cleanNameA.localeCompare(cleanNameB, 'th');
      });

      return filtered;
    });

    const { user } = getUser();

    const playerStore = usePlayerStore();

    watch(() => playerStore.userData, (data) => {
      const newIsAdmin = data?.admin === true;
      if (newIsAdmin !== isAdmin.value && initialFetchDone.value) {
        isAdmin.value = newIsAdmin;
        fetchMaps();
      } else {
        isAdmin.value = newIsAdmin;
      }
    }, { immediate: true });

    // Fetch Maps from Firestore
    const fetchMaps = async () => {
      try {
        isLoading.value = true;
        error.value = null;
        let mapsQuery = projectFirestore.collection('maps');
        if (!isAdmin.value) {
          mapsQuery = mapsQuery.where('isActive', '==', true);
        }
        const mapsSnapshot = await mapsQuery.get();

        maps.value = mapsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: typeof data.name === 'string' ? data.name : 'Unknown',
            type: data.type || 'สถานที่',
            isActive: data.isActive !== undefined ? data.isActive : true,
            ...data
          };
        });
      } catch (err) {
        error.value = 'ไม่สามารถดึงข้อมูลแผนที่ได้';
      } finally {
        isLoading.value = false;
      }
    };

    // Apply filters
    const applyFilters = () => {
      fetchMaps();
    };

    // Reset and fetch maps
    const resetAndFetch = () => {
      toggleModal.value = '';
      selectedMap.value = null;
      fetchMaps();
    };

    // Handle image loading errors
    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/150?text=Image+Not+Found';
    };

    // Show map details modal
    const showDetails = (map) => {
      selectedMap.value = map;
      toggleModal.value = 'ShowDetails';
    };

    // Edit map modal
    const editMap = (map) => {
      selectedMap.value = map;
      toggleModal.value = 'EditMap';
    };

    // Close details modal
    const closeDetailsModal = () => {
      selectedMap.value = null;
      toggleModal.value = '';
    };

    // Fetch maps on component mount
    onMounted(async () => {
      await fetchMaps();
      initialFetchDone.value = true;
    });

    return { 
      toggleModal, 
      maps, 
      filteredMaps,
      selectedMap,
      fetchMaps,
      handleImageError,
      showDetails,
      editMap,
      closeDetailsModal,
      isLoading,
      isAddingMap,
      isAdmin,
      filterType,
      resetAndFetch,
      applyFilters,
      error
    };
  },
};
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmap.png?alt=media&token=4b60959e-00bc-41ff-85d5-d29d235f18e2') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
}
#style-1 {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

#style-1::-webkit-scrollbar {
  width: 5px;
}

#style-1::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}

#style-1::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}

#style-1::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.tooltip {
  visibility: hidden;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: #e0e7ff;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  margin-bottom: 0.5rem;
}

.group:hover .tooltip {
  visibility: visible;
  opacity: 1;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.animate-spin-pulse {
  animation: spin-pulse 1.2s ease-in-out infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}

.modal {
  animation: modalFade 0.2s ease-out;
}
@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}

.click-scale:active {
  transform: scale(0.98);
  transition: transform 0.1s ease;
}

.group:hover {
  box-shadow: 0 0 10px rgba(79, 70, 229, 0.3);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 639px) {
  .text-sm {
    font-size: 0.75rem;
  }
  .text-base {
    font-size: 0.875rem;
  }
  .p-4 {
    padding: 0.75rem;
  }
  .h-48 {
    height: 10rem;
  }
}
</style>